import { FunctionComponent, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckoutDetails as CheckoutDetailsReusable, CheckoutDetailsTranslations, FormData, DefaultValueData } from '@cdaas/reusable-components-daas';
import { Headline, Title, BodyText, Div, Page, Loader } from '@gaiads/telia-react-component-library';
import { InvoicingAccountOptionResponseContents, UserRole } from '@cdaas/api-clients/dist/asset-user-openapi';
import _ from 'lodash';

import { COUNTRY_CALLING_CODES } from '../../../../utils/constants';
import { RootState } from '../../../../store/reducers';
import { shopSetDeliveryDetails } from '../../../../store/actions';
import { Delivery, User } from '../../../../types';
import { getInvoicingAccounts, hasUserARequiredRole, parsingHelpers, setStorageItems, typeGuards } from '../../../../utils';
import CheckoutProductCart from '../CheckoutProductCart/CheckoutProductCart';

import classes from './CheckoutDetails.module.css';
import { InvoicingAccountSelectionTranslations } from '@cdaas/reusable-components-daas/dist/src/components/InvoicingAccountSelection/types';

export interface CheckoutDetailsProps {
  isMobile?: boolean;
}

const CheckoutDetails: FunctionComponent<CheckoutDetailsProps> = ({ isMobile }) => {
  const history = useHistory();

  const { t: tShop } = useTranslation('shop');
  const { t: tGlobal } = useTranslation('globalComponents');

  const user = useSelector((state: RootState) => state.authentication.user, _.isEqual);
  const deliveryDetailsStore = useSelector((state: RootState) => state.shop.delivery, _.isEqual);

  const [deliveryDetails, setDeliveryDetails] = useState<Delivery>();
  const [defaultDeliveryValues, setDefaultDeliveryValues] = useState<DefaultValueData>();
  const [organizationInvoicingAccounts, setOrganizationInvoicingAccounts] = useState<InvoicingAccountOptionResponseContents[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [inheritedInvoicingAccount, setInheritedInvoicingAccount] = useState<string>('');

  const orderingForOrganization = useSelector((state: RootState) => state.shop.orderForOrganization);
  const orderingOnBehalfUserData = useSelector((state: RootState) => state.shop.orderOnBehalfUser);
  const selectedOrganization = useSelector((state: RootState) => state.authentication.selectedOrganization);
  const ownOrganization = useSelector((state: RootState) => state.authentication.user?.organization);
  const dispatch = useDispatch();

  const userHasAdminRole = hasUserARequiredRole([UserRole.Admin]);
  const userHasOrdererRole = hasUserARequiredRole([UserRole.Orderer]);
  const showLocationReference = userHasAdminRole || userHasOrdererRole;
  const showInvoicingAccount = userHasAdminRole || userHasOrdererRole;

  const initDeliveryDetailsFields = (userOrDeliveryDetails: User | Delivery) => {
    if (typeGuards.isUser(userOrDeliveryDetails)) {
      const parsedNumber = userOrDeliveryDetails.phone_number
        ? parsingHelpers.extractPartsFromFullPhoneNumber(userOrDeliveryDetails.phone_number)
        : undefined;

      setDeliveryDetails({
        deliveryContact: {
          first_name: userOrDeliveryDetails.given_name ?? '',
          last_name: userOrDeliveryDetails.family_name ?? '',
          email: userOrDeliveryDetails.email ?? '',
          phone_number: parsedNumber?.nationalNumber.toString() ?? '',
          country_calling_code: parsedNumber?.countryCallingCode.toString() ?? '',
          title: '',
        },
        deliveryAddress: {
          name: '',
          address1: userOrDeliveryDetails.user_group?.default_delivery_address?.address1 ?? '',
          address2: userOrDeliveryDetails.user_group?.default_delivery_address?.address2 ?? '',
          street: userOrDeliveryDetails.user_group?.default_delivery_address?.street ?? '',
          city: userOrDeliveryDetails.user_group?.default_delivery_address?.city ?? '',
          zip: userOrDeliveryDetails.user_group?.default_delivery_address?.zip ?? '',
          country: userOrDeliveryDetails.user_group?.default_delivery_address?.country ?? '',
        },
        isHomeDelivery: false,
        locationReference: '',
        invoicingAccountNumber: '',
      });
    } else {
      setDeliveryDetails(userOrDeliveryDetails);
    }
  };

  useEffect(() => {
    if (user && (deliveryDetailsStore?.deliveryAddress || deliveryDetailsStore?.deliveryContact)) {
      initDeliveryDetailsFields(deliveryDetailsStore);
    }
  }, [user, deliveryDetailsStore]);

  useEffect(() => {
    const values: DefaultValueData = {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      country_calling_code: '',
      street: user?.user_group?.default_delivery_address?.street || '',
      address1: user?.user_group?.default_delivery_address?.address1 || '',
      address2: user?.user_group?.default_delivery_address?.address2 || '',
      // Set organization country for now because there's only one supported country
      country: user?.organization?.locale.country || '',
      zip: user?.user_group?.default_delivery_address?.zip || '',
      city: user?.user_group?.default_delivery_address?.city || '',
      location_reference: '',
      invoicing_account_number: '',
    };
    if (orderingOnBehalfUserData.status) {
      const parsedNumber = orderingOnBehalfUserData.userToOrderFor?.phone_number
        ? parsingHelpers.extractPartsFromFullPhoneNumber(orderingOnBehalfUserData.userToOrderFor.phone_number)
        : undefined;
      values.first_name = orderingOnBehalfUserData.userToOrderFor?.given_name || '';
      values.last_name = orderingOnBehalfUserData.userToOrderFor?.family_name || '';
      values.email = orderingOnBehalfUserData.userToOrderFor?.email || '';
      values.phone_number = parsedNumber?.nationalNumber.toString() || '';
      values.country_calling_code = parsedNumber?.countryCallingCode.toString() || '';
    } else {
      // Show user information also when ordering on-behalf of organization
      const parsedNumber = user?.phone_number ? parsingHelpers.extractPartsFromFullPhoneNumber(user.phone_number) : undefined;
      values.first_name = user?.given_name || '';
      values.last_name = user?.family_name || '';
      values.email = user?.email || '';
      values.phone_number = parsedNumber?.nationalNumber.toString() || '';
      values.country_calling_code = parsedNumber?.countryCallingCode.toString() || '';
    }
    setDefaultDeliveryValues(values);

    setInheritedInvoicingAccount(
      orderingForOrganization
        ? selectedOrganization?.call_off_number || ownOrganization?.call_off_number || ''
        : orderingOnBehalfUserData.userToOrderFor
          ? orderingOnBehalfUserData.userToOrderFor.invoicing_account?.name ||
            orderingOnBehalfUserData.userToOrderFor.inherited_invoicing_account.name
          : user?.invoicing_account?.name || user?.inherited_invoicing_account.name || '',
    );
  }, [user, orderingForOrganization, orderingOnBehalfUserData, selectedOrganization?.call_off_number, ownOrganization?.call_off_number]);

  useEffect(() => {
    async function fetchData() {
      const invoicingAccounts = await getInvoicingAccounts();
      setOrganizationInvoicingAccounts(invoicingAccounts || []);
      setLoading(false);
    }

    if (showInvoicingAccount) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [showInvoicingAccount]);

  const handleSavingDeliveryDetailsData = (data: FormData): void => {
    const address = {
      name: '',
      address1: data.address1,
      address2: data.address2,
      street: data.street,
      city: data.city,
      zip: data.zip,
      country: data.country,
    };
    const contact = {
      title: '',
      first_name: data.first_name,
      last_name: data.last_name,
      phone_number: data.phone_number,
      country_calling_code: data.country_calling_code,
      email: data.email,
    };
    const locationReference = data.location_reference.trim() === '' ? null : data.location_reference;
    const invoicingAccount = data.invoicing_account_number.trim() === '' ? null : data.invoicing_account_number;
    dispatch(shopSetDeliveryDetails(address, contact, data.homeDelivery, locationReference, invoicingAccount));
    setStorageItems(['tDelivery'], [{ contact, address, isHomeDelivery: data.homeDelivery }], 'session');
  };

  const onSubmit = (data: FormData): void => {
    handleSavingDeliveryDetailsData(data);
    history.push('/shop/checkout/delivery');
  };

  const handleGoBackClick = (): void => {
    history.goBack();
  };

  const translation: CheckoutDetailsTranslations = {
    employeeTitle: tShop('checkout.detailsPage.employeeTitle'),
    billingTitle: tShop('checkout.detailsPage.billingTitle'),
    deliveryTitle: tShop('checkout.detailsPage.deliveryTitle'),
    billingTextOne: tShop('checkout.detailsPage.billingTextOne'),
    billingTextTwo: tShop('checkout.detailsPage.billingTextTwo'),
    deliveryText: tShop('checkout.detailsPage.deliveryText'),
    deliveryQuestion: tShop('checkout.detailsPage.deliveryQuestion'),
    firstName: tShop('checkout.detailsPage.form.firstName'),
    lastName: tShop('checkout.detailsPage.form.lastName'),
    email: tShop('checkout.detailsPage.form.email'),
    phoneNbr: tShop('checkout.detailsPage.form.phoneNbr'),
    address1: tShop('checkout.detailsPage.form.address1'),
    address2: tShop('checkout.detailsPage.form.address2'),
    streetAddress: tShop('checkout.detailsPage.form.streetAddress'),
    postalCode: tShop('checkout.detailsPage.form.postalCode'),
    city: tShop('checkout.detailsPage.form.city'),
    country: tShop('checkout.detailsPage.form.country'),
    countryPlaceholder: tShop('checkout.detailsPage.form.countryPlaceholder'),
    companyDeliveryOption: tShop('checkout.detailsPage.form.companyDeliveryOption'),
    companyDeliveryOptionDetails: tShop('checkout.detailsPage.form.companyDeliveryOptionDetails'),
    homeDeliveryOption: tShop('checkout.detailsPage.form.homeDeliveryOption'),
    homeDeliveryOptionDetails: tShop('checkout.detailsPage.form.homeDeliveryOptionDetails'),
    location: tShop('checkout.detailsPage.form.location'),
    locationReference: tShop('checkout.detailsPage.form.locationReference'),
    locationReferenceHint: tShop('checkout.detailsPage.form.locationReferenceHint'),
    invoicingAccountTitle: tShop('checkout.detailsPage.form.invoicingAccount.title'),
    required: tShop('checkout.detailsPage.form.rules.required'),
    invalidEmail: tShop('checkout.detailsPage.form.rules.invalidEmail'),
    invalidPhoneNumber: tShop('checkout.detailsPage.form.rules.invalidPhoneNumber'),
    invalidPostalCode: tShop('checkout.detailsPage.form.rules.invalidPostalCode'),
    nextButton: tShop('checkout.detailsPage.nextButton'),
    cancelButton: tShop('checkout.detailsPage.cancelButton'),
    Finland: tGlobal('countries.Finland'),
    Sweden: tGlobal('countries.Sweden'),
    Norway: tGlobal('countries.Norway'),
    Denmark: tGlobal('countries.Denmark'),
    UnitedKingdom: tGlobal('countries.UnitedKingdom'),
  };

  const invoicingAccountTranslations: InvoicingAccountSelectionTranslations = {
    dropdownLabel: tShop('checkout.detailsPage.form.invoicingAccount.dropdownLabel'),
    clearSelection: tShop('checkout.detailsPage.form.invoicingAccount.clearSelection'),
    helperText: tShop('checkout.detailsPage.form.invoicingAccount.helperText'),
    noOptionsFoundInfo: tShop('checkout.detailsPage.form.invoicingAccount.noOptionsFoundInfo'),
  };

  const renderOrderingForOrganization = () => (
    <>
      <Title size="xs" margin={{ top: 'lg', bottom: 'xs' }} color={'black'}>
        {tShop('checkout.detailsPage.organizationTitle')}
      </Title>
      <Div className={classes['employee-or-organization-title']} margin={{ top: 'lg', bottom: 'xlg' }} color={'gray800'}>
        {selectedOrganization?.name || ownOrganization?.name}
      </Div>
    </>
  );

  const renderOrderingOnBehalfUser = () => (
    <>
      <Title size="xs" margin={{ top: 'lg', bottom: 'xs' }} color={'black'}>
        {tShop('checkout.detailsPage.employeeTitle')}
      </Title>
      <Div className={classes['employee-or-organization-title']} margin={{ top: 'lg', bottom: 'xs' }} color={'gray800'}>
        {`${orderingOnBehalfUserData.userToOrderFor?.given_name ?? ''} ${orderingOnBehalfUserData.userToOrderFor?.family_name ?? ''}`}
      </Div>
      <BodyText size="sm" color="gray500">
        {`${orderingOnBehalfUserData.userToOrderFor?.email}, ${orderingOnBehalfUserData.userToOrderFor?.phone_number}`}
      </BodyText>
    </>
  );

  if (loading) {
    return (
      <Page>
        <Page.Row alignTextToCenter margin={{ vertical: 'xxlg' }}>
          <Loader />
        </Page.Row>
      </Page>
    );
  }

  const isOnBehalfOrder = orderingForOrganization || orderingOnBehalfUserData.status;

  return (
    <>
      <Headline size={'md'}>{tShop('checkout.detailsPage.headline')}</Headline>
      <Div shown={isOnBehalfOrder}>
        {orderingForOrganization && renderOrderingForOrganization()}
        {orderingOnBehalfUserData.status && renderOrderingOnBehalfUser()}
      </Div>
      <CheckoutDetailsReusable
        handleBackClick={handleGoBackClick}
        handleNextClick={onSubmit}
        handleSavingDataOnBlurSubmitRestore={handleSavingDeliveryDetailsData}
        // Support only organization's country for now
        countries={COUNTRY_CALLING_CODES.filter(ccc => ccc.value.toLowerCase() === user?.organization?.locale.country.toLowerCase())}
        countryCodes={COUNTRY_CALLING_CODES}
        deliveryDetails={deliveryDetails}
        defaultValues={defaultDeliveryValues}
        prefillUserGroupDefaultIfDeliveryDetailsNotAvailable={true}
        showLocationReference={showLocationReference}
        showInvoicingAccount={showInvoicingAccount}
        inheritedInvoicingAccount={inheritedInvoicingAccount}
        invoicingAccounts={organizationInvoicingAccounts}
        translations={translation}
        invoicingAccountTranslations={invoicingAccountTranslations}
        ProductCart={isMobile ? <CheckoutProductCart isMobile /> : undefined}
      />
    </>
  );
};

export default CheckoutDetails;
